// Step 1 of the import wizard: choose the file, and say what the API found in
// it (sheet, header row, rows, columns). Draws only what it is given and
// calls back; the shell reads the file and holds the answers.

import { useEffect, useRef, useState } from 'react'
import { ImportError } from '../../components/ImportUndo'
import { Icon } from '../../components/ui'
import type { ImportAnalysis } from '../../lib/api'
import { IMPORT_BANNER, plural } from '../../lib/importWords'

// What the file was recognised as, if anything was applied: the saved mapping
// its headers matched (with how often it was used), or else a known layout.
export function recognisedAs(a: ImportAnalysis | null): { name: string; times: number; review: boolean } | null {
  if (!a) return null
  if (a.savedMapping?.name) return { name: a.savedMapping.name, times: a.savedMapping.timesUsed ?? 0, review: false }
  if (a.layout?.applied && a.layout.label) return { name: a.layout.label, times: 0, review: !a.layout.checked }
  return null
}

export function FileStep({
  analysis,
  busy,
  error,
  onFile,
  onTemplate,
  onReread,
  onNext,
  onAnother,
  onChangeMapping,
  fileHeld = true,
}: {
  analysis: ImportAnalysis | null
  busy: boolean
  error: unknown
  onFile: (f: File) => void
  onTemplate: (format: 'xlsx' | 'csv') => void
  onReread: (opts: { sheet?: string; headerRow?: number }) => void
  onNext: () => void
  onAnother: () => void
  // "Change" beside "Recognised": Match columns, every row shown.
  onChangeMapping?: () => void
  // False once the page was refreshed: the answers are kept in the tab, the
  // file itself is not, so it cannot be read again with another sheet or row.
  fileHeld?: boolean
}) {
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const r = analysis?.reading
  const recognised = recognisedAs(analysis)
  const [headerRow, setHeaderRow] = useState(String(r?.headerRow || 1))
  useEffect(() => setHeaderRow(String(r?.headerRow || 1)), [r?.headerRow, analysis?.importId])
  const banner = analysis?.banner || IMPORT_BANNER
  const sheets = r?.sheets ?? []

  const found = r
    ? [r.sheet && sheets.length > 0 ? 'Sheet ' + r.sheet : '', plural(r.rows, 'vendor'), plural(r.columns, 'column')]
        .filter(Boolean)
        .join(', ')
    : ''

  return (
    <>
      <p className="note imw-banner" role="note">
        <Icon name="shield" size={18} />
        <span>{banner}</span>
      </p>

      {!analysis && (
        <>
          <section className="panel">
            <div
              className={'drop' + (dragging ? ' on' : '')}
              onDragOver={(e) => {
                e.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragging(false)
                const f = e.dataTransfer.files?.[0]
                if (f && !busy) onFile(f)
              }}
              onClick={() => input.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Choose the file with your vendors"
              aria-busy={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  input.current?.click()
                }
              }}
            >
              <Icon name="file" size={28} />
              <strong>{busy ? 'Reading the file' : 'Drop the file here, or click to choose'}</strong>
              <span>Excel, CSV or a vendor list from the tool you use today</span>
              <input
                ref={input}
                type="file"
                accept=".csv,.tsv,.txt,.xlsx,.json,text/csv,text/tab-separated-values,text/plain,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="vh"
                aria-label="Choose the file"
                data-action="tprm.import.a01"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) onFile(f)
                }}
              />
            </div>
            <ImportError error={error} />
          </section>
          <div className="need-template">
            <span>
              <strong>Need a template?</strong> Excel or CSV, one vendor per row
            </span>
            <span className="need-template-actions">
              <button type="button" data-action="tprm.import.a04" onClick={() => onTemplate('xlsx')}>
                <Icon name="file" size={15} /> Excel template
              </button>
              <button type="button" data-action="tprm.import.a04" onClick={() => onTemplate('csv')}>
                <Icon name="file" size={15} /> CSV template
              </button>
            </span>
          </div>
        </>
      )}

      {analysis && r && (
        <section className="panel" aria-labelledby="imw-file">
          <h2 id="imw-file" style={{ margin: 0 }}>
            {analysis.fileName}
          </h2>
          <p className="imw-lead" style={{ margin: '2px 0 10px' }}>
            {found}
          </p>
          {!r.headerFound && (
            <div className="note warn" style={{ marginBottom: 10 }}>
              The row with the column names was not found. Give its number below and read the file again.
            </div>
          )}
          {/* Said only when a saved mapping or a layout was used: one
              recognised and not applied matched nothing, and saying so would
              mislead (plan 4.7). "Change" opens Match columns with every row. */}
          {recognised && (
            <p className="imw-recognised">
              <Icon name="check" size={14} />
              <span>
                Recognised: {recognised.name}
                {recognised.times > 0 ? ', used ' + plural(recognised.times, 'time') : ''}.
                {recognised.review && ' Review the matches on the next step.'}
              </span>
              {onChangeMapping && (
                <button type="button" className="linkish" onClick={onChangeMapping} disabled={busy || analysis.columns.length === 0}>
                  Change<span className="vh"> the matches</span>
                </button>
              )}
            </p>
          )}
          {(r.notes ?? []).length > 0 && (
            <ul className="p1-inline-list" style={{ marginBottom: 10 }}>
              {(r.notes ?? []).map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}
          {!fileHeld && (
            <p className="imw-small" style={{ margin: '0 0 10px' }}>
              To read the file again with another sheet or header row, choose it again with Replace file.
            </p>
          )}
          <div className="imw-found" hidden={!fileHeld}>
            {sheets.length > 1 && (
              <div className="field">
                <label htmlFor="imw-sheet">Sheet</label>
                <select
                  id="imw-sheet"
                  value={String(Math.max(0, sheets.indexOf(r.sheet ?? '')))}
                  disabled={busy}
                  onChange={(e) => onReread({ sheet: sheets[Number(e.target.value)] })}
                >
                  {sheets.map((s, i) => (
                    <option key={i} value={String(i)}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <form
              className="sw-row"
              onSubmit={(e) => {
                e.preventDefault()
                const n = parseInt(headerRow, 10)
                if (n >= 1) onReread({ sheet: r.sheet || undefined, headerRow: n })
              }}
            >
              <label htmlFor="imw-header">The column names are on row</label>
              <input
                id="imw-header"
                type="number"
                min={1}
                inputMode="numeric"
                value={headerRow}
                onChange={(e) => setHeaderRow(e.target.value)}
              />
              <button type="submit" disabled={busy || String(r.headerRow) === headerRow.trim()}>
                {busy ? 'Reading' : 'Read again'}
              </button>
            </form>
          </div>
          <ImportError error={error} />
          <div className="imw-step-foot">
            <button type="button" onClick={onAnother} disabled={busy}>
              Replace file
            </button>
            <button
              type="button"
              className="primary imw-push"
              disabled={busy || analysis.columns.length === 0}
              onClick={onNext}
            >
              Next
            </button>
            {analysis.columns.length === 0 && (
              <p className="imw-why-not">No columns were found. Give the number of the row that holds the column names.</p>
            )}
          </div>
        </section>
      )}
    </>
  )
}
